/**
 * DomainServerConfigTab — 收件域右侧"服务器配置"Tab
 * 配置该域出站 HELO 主机名（与 A 记录、反向解析 PTR 一致，提升外发信誉、避免被判垃圾邮件）。
 * 该值按收件域独立配置，实时生效；为空时出站回退到全局 hostname。
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Field,
  InfoLabel,
  Input,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components'
import { SaveRegular, ServerRegular } from '@fluentui/react-icons'
import { domainsApi } from '@/api/domains'
import type { Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '16px',
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
})

export default function DomainServerConfigTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const [helo, setHelo] = useState<string>(domain.heloDomain ?? '')
  const [dirty, setDirty] = useState(false)

  const saveMutation = useMutation({
    mutationFn: async () => {
      // 后端 update_domain 为整行覆盖更新，需携带全部字段
      await domainsApi.update(domain.id, {
        name: domain.name,
        description: domain.description,
        max_quota_bytes: domain.max_quota_bytes,
        max_users: domain.max_users,
        default_forward_email: domain.defaultForwardEmail,
        helo_domain: helo.trim() || null,
      })
      return domain.id
    },
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: ['domains'] })
      await queryClient.invalidateQueries({ queryKey: ['domain', id] })
      setDirty(false)
      toast.success('已保存', `${domain.name} 的服务器主机名已更新，出站实时生效`)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  return (
    <div className={styles.root}>
      <Card size="small">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ServerRegular />
          <Text style={{ fontWeight: 600 }}>服务器主机名（{domain.name}）</Text>
        </div>
        <MessageBar intent="info" style={{ marginTop: 10, marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>按域名生效</MessageBarTitle>
            该值即本域对外发信时宣告的 HELO/EHLO 主机名。需与该域名的 A 记录、以及服务器公网 IP 的反向解析（PTR）三者一致，否则外发邮件易被接收方判为垃圾邮件。留空时回退到服务器全局 hostname。
          </MessageBarBody>
        </MessageBar>
        <Field
          label={<InfoLabel info="通常为 mail.你的域名，例如 mail.500p.tech。需满足：A 记录 mail.你的域名 → 本服务器 IP，且该 IP 的反向解析 PTR 也指向同一主机名。">HELO 主机名</InfoLabel>}
          hint="例如 mail.500p.tech"
        >
          <Input
            value={helo}
            onChange={(_, d) => {
              setHelo(d.value)
              setDirty(true)
            }}
            placeholder="mail.500p.tech"
            style={{ maxWidth: '320px' }}
          />
        </Field>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </Card>

      <Text className={styles.hint}>
        提示：配置后可用 <code>dig -x &lt;服务器公网IP&gt;</code> 确认反向解析已指向此处填写的主机名；并确认 <code>dig A &lt;主机名&gt;</code> 解析到同一 IP。
      </Text>
    </div>
  )
}
